export function BeingHonestAboutCondition() {
  return (
    <section className="py-20 px-4 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Being Honest About Your Property's As-Is Condition
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Transparency about your property's true condition is essential for accurate valuation and successful transactions. Honesty saves time, builds trust, and often leads to better outcomes than attempting to hide defects.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/c9c841f6a3fc9ca1c2ca433b8ddd12bd.jpg"
              alt="Being honest about property condition in real estate"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Why Honesty Is the Best Policy</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Professional investors and buyers will discover any issues during their due diligence process. Attempting to hide problems doesn't work—it only wastes everyone's time and damages your credibility. Honest disclosure from the start leads to smoother transactions and often faster closings.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Many sellers worry that disclosing problems will reduce their property's value. In reality, experienced buyers already assume issues exist and price accordingly. Being upfront about known problems actually builds trust and can lead to better offers because buyers appreciate transparency.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Key Principle:</h4>
              <p className="text-gray-800 text-lg leading-relaxed">
                Buyers will find the problems anyway. Honesty saves time, builds trust, and prevents deals from falling apart during due diligence.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Benefits of Honest Condition Disclosure</h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-full mb-4">
                <i className="ri-time-line text-green-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Faster Transactions</h4>
              <p className="text-gray-600 leading-relaxed">
                No surprises during due diligence means fewer delays, renegotiations, or deal cancellations. Honest sellers close faster.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-full mb-4">
                <i className="ri-shield-check-line text-teal-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Legal Protection</h4>
              <p className="text-gray-600 leading-relaxed">
                Full disclosure protects you from future legal claims. Hiding known defects can lead to lawsuits after closing.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-blue-100 rounded-full mb-4">
                <i className="ri-hand-heart-line text-blue-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Builds Trust</h4>
              <p className="text-gray-600 leading-relaxed">
                Transparency establishes credibility with buyers, leading to smoother negotiations and better working relationships.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-purple-100 rounded-full mb-4">
                <i className="ri-money-dollar-circle-line text-purple-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Attracts Serious Buyers</h4>
              <p className="text-gray-600 leading-relaxed">
                Honest listings attract experienced investors who appreciate transparency and are ready to close quickly.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mb-4">
                <i className="ri-checkbox-circle-line text-orange-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Accurate Pricing</h4>
              <p className="text-gray-600 leading-relaxed">
                Knowing true condition allows for accurate valuation from the start, avoiding price reductions later.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-md">
              <div className="w-12 h-12 flex items-center justify-center bg-red-100 rounded-full mb-4">
                <i className="ri-emotion-happy-line text-red-600 text-xl"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-3 text-lg">Peace of Mind</h4>
              <p className="text-gray-600 leading-relaxed">
                No stress about hidden issues being discovered. You can proceed with confidence and clear conscience.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What to Disclose About Your Property</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Be thorough in disclosing all known issues, even if you think they're minor. Here's what professional buyers want to know about:
            </p>
            <div className="space-y-4">
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Structural Issues</h4>
                <p className="text-gray-600">Foundation cracks, settling, structural repairs, roof condition, water intrusion</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Major Systems</h4>
                <p className="text-gray-600">Age and condition of HVAC, plumbing, electrical, water heaters, appliances</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Deferred Maintenance</h4>
                <p className="text-gray-600">Known repairs needed, items requiring replacement, maintenance backlog</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Code Violations</h4>
                <p className="text-gray-600">Any known violations, pending citations, required upgrades or corrections</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Environmental Issues</h4>
                <p className="text-gray-600">Mold, asbestos, lead paint, underground tanks, soil contamination</p>
              </div>
              <div className="bg-white rounded-lg p-5 shadow-md border-l-4 border-teal-600">
                <h4 className="font-bold text-gray-900 mb-2">Tenant Issues</h4>
                <p className="text-gray-600">Problem tenants, lease violations, pending evictions, below-market rents</p>
              </div>
            </div>
          </div>
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=property-inspection-checklist-building-assessment-detailed-evaluation-inspector-examining-property-condition-report-documentation-thorough-inspection-professional-property-evaluation-disclosure-documentation&width=800&height=600&seq=disclosure-checklist-001&orientation=landscape"
              alt="Property condition disclosure and inspection"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-lg p-8 lg:p-12 text-white mb-12">
          <h3 className="text-3xl font-bold mb-6 text-center">The Cost of Hiding Problems</h3>
          <p className="text-lg text-center mb-10 max-w-3xl mx-auto opacity-90">
            Attempting to conceal property issues can have serious consequences that far outweigh any perceived short-term benefits:
          </p>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-close-circle-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Deal Cancellations</h4>
                  <p className="opacity-90">Buyers discover issues during inspections and walk away, wasting weeks or months of your time</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-arrow-down-circle-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Price Reductions</h4>
                  <p className="opacity-90">Buyers demand larger discounts when they discover undisclosed problems during due diligence</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-scales-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Legal Liability</h4>
                  <p className="opacity-90">Failure to disclose known defects can result in lawsuits, damages, and legal fees after closing</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-user-unfollow-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Damaged Reputation</h4>
                  <p className="opacity-90">Word spreads in real estate circles about dishonest sellers, making future transactions difficult</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-time-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Extended Marketing Time</h4>
                  <p className="opacity-90">Properties with undisclosed issues that surface repeatedly fail to close, sitting on market longer</p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-emotion-sad-line text-white text-xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-lg mb-2">Stress and Anxiety</h4>
                  <p className="opacity-90">Constant worry about issues being discovered creates unnecessary stress throughout the process</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 lg:p-12 text-white">
          <h3 className="text-3xl font-bold mb-6 text-center">How Professional Investors View As-Is Properties</h3>
          <p className="text-lg text-center mb-10 max-w-3xl mx-auto opacity-90">
            Experienced apartment building investors actually prefer honest as-is properties because they understand the true opportunity:
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4">What Investors Appreciate</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Transparent sellers who provide complete information upfront</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Realistic pricing that reflects actual property condition</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Detailed documentation of known issues and needed repairs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Sellers who understand value-add opportunities exist</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Quick closings without repair negotiations or contingencies</span>
                </li>
              </ul>
            </div>

            <div className="bg-white/10 rounded-lg p-6">
              <h4 className="text-xl font-bold mb-4">Why As-Is Can Be Better</h4>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>No need to spend money on repairs before selling</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Investors have contractors and get better pricing on repairs</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Faster transactions with fewer contingencies</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Cash buyers don't require perfect condition for financing</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-300 text-xl mt-1 flex-shrink-0"></i>
                  <span>Sell now rather than managing repairs for months</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-10 bg-white/10 rounded-lg p-6 max-w-4xl mx-auto">
            <p className="text-lg text-center leading-relaxed">
              <strong>Bottom Line:</strong> Professional investors buy properties in any condition. Being honest about issues doesn't scare them away—it builds trust and leads to faster, smoother transactions. The right buyer will value your transparency and make a fair offer based on the true condition.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}